import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { UserPlus, Users, Dumbbell, CheckCircle, AlertTriangle, Home, UsersRound, Mail } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [existingUser, setExistingUser] = useState(null);
  const [requiresRegistration, setRequiresRegistration] = useState(false);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  
  // Registration form
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (token) {
      validateInvitation();
    } else {
      setError('Token de invitación no proporcionado');
      setLoading(false);
    }
  }, [token]);

  const validateInvitation = async () => {
    try {
      const res = await axios.get(`${API}/invitations/validate/${token}`);
      setInvitation(res.data.invitation);
      setExistingUser(res.data.existing_user);
      setRequiresRegistration(res.data.requires_registration);
      
      // Pre-fill name if provided
      if (res.data.invitation.invitee_name) {
        setFormData(prev => ({ ...prev, name: res.data.invitation.invitee_name }));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invitación no válida o expirada');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (requiresRegistration) {
      if (!formData.name.trim()) {
        toast.error('Ingresa tu nombre');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Las contraseñas no coinciden');
        return;
      }
    }
    
    setAccepting(true);
    try {
      const payload = { token };
      if (requiresRegistration) {
        payload.name = formData.name;
        payload.password = formData.password;
      }
      
      const res = await axios.post(`${API}/invitations/accept`, payload);
      setAccepted(true);
      toast.success(res.data.message);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate(res.data.redirect_to || '/login');
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aceptar invitación');
    } finally {
      setAccepting(false);
    }
  };

  const getInvitationIcon = () => {
    if (!invitation) return Users;
    if (invitation.type === 'coach') return Dumbbell;
    if (invitation.group_name?.includes('Family') || invitation.context_name?.includes('Family')) return Home;
    return UsersRound;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="card-default max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-white text-xl font-medium mb-2">Invitación no válida</h2>
            <p className="text-zinc-400 mb-6">{error}</p>
            <Link to="/login">
              <Button className="btn-secondary">
                Ir a Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="card-default max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-white text-xl font-medium mb-2">¡Bienvenido!</h2>
            <p className="text-zinc-400 mb-4">
              {requiresRegistration 
                ? 'Tu cuenta ha sido creada. Redirigiendo al login...'
                : 'Te has unido exitosamente. Redirigiendo...'
              }
            </p>
            <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = getInvitationIcon();

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="card-default max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
            invitation?.type === 'coach' ? 'bg-primary/20' : 'bg-amber-500/20'
          }`}>
            <Icon className={`w-10 h-10 ${
              invitation?.type === 'coach' ? 'text-primary' : 'text-amber-400'
            }`} />
          </div>
          <CardTitle className="text-white font-teko text-2xl">
            INVITACIÓN A ONOR CODE
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="bg-zinc-800/50 rounded p-4 text-center">
            <p className="text-zinc-400 text-sm mb-1">
              {invitation?.inviter_name || 'Alguien'} te ha invitado a:
            </p>
            <p className="text-white text-lg font-medium">
              {invitation?.context_name || 'ONOR Code'}
            </p>
            {invitation?.message && (
              <p className="text-zinc-500 text-sm mt-2 italic">
                "{invitation.message}"
              </p>
            )}
          </div>

          {/* Email */}
          <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded">
            <Mail className="w-5 h-5 text-zinc-500" />
            <span className="text-zinc-300">{invitation?.invitee_email}</span>
          </div>

          {/* Registration Form (if new user) */}
          {requiresRegistration && (
            <div className="space-y-4 pt-2">
              <p className="text-zinc-400 text-sm text-center">
                Crea tu cuenta para aceptar la invitación
              </p>
              
              <div>
                <Label className="text-zinc-400 text-xs">Nombre completo</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Tu nombre"
                  data-testid="input-name"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400 text-xs">Contraseña</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Mínimo 6 caracteres"
                  data-testid="input-password"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400 text-xs">Confirmar contraseña</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Repite tu contraseña"
                  data-testid="input-confirm-password"
                />
              </div>
            </div>
          )}

          {/* Existing User Info */}
          {existingUser && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-4 text-center">
              <CheckCircle className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
              <p className="text-emerald-400 text-sm">
                Ya tienes cuenta como <strong>{existingUser.name}</strong>
              </p>
            </div>
          )}

          {/* Accept Button */}
          <Button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full btn-primary py-6"
            data-testid="accept-invitation-btn"
          >
            {accepting ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <UserPlus className="w-5 h-5 mr-2" />
            )}
            {requiresRegistration ? 'CREAR CUENTA Y ACEPTAR' : 'ACEPTAR INVITACIÓN'}
          </Button>

          {/* Login Link */}
          <p className="text-center text-zinc-500 text-sm">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-primary hover:text-primary">
              Inicia sesión
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
